import React, { useState, useMemo } from "react";
import {
  RadarChart,
  PolarGrid,
  PolarAngleAxis,
  PolarRadiusAxis,
  Radar,
  ResponsiveContainer,
} from "recharts";

const SCALE = [
  { v: 0, label: "Not in place" },
  { v: 1, label: "Ad hoc / informal" },
  { v: 2, label: "Defined, inconsistently applied" },
  { v: 3, label: "Institutionalized & monitored" },
];

const CATEGORIES = [
  {
    id: "quality",
    label: "Data Quality & Accuracy",
    questions: [
      {
        id: "q1",
        text: "We have a defined process for measuring data accuracy and completeness.",
      },
      {
        id: "q2",
        text: "Data quality issues are tracked and resolved on a regular cadence.",
      },
      {
        id: "q3",
        text: "Business users trust the data in our core reporting systems.",
      },
    ],
  },
  {
    id: "metadata",
    label: "Metadata & Cataloging",
    questions: [
      {
        id: "q4",
        text: "We maintain a data catalog documenting what data exists and where it lives.",
      },
      {
        id: "q5",
        text: "Data lineage is documented for our most critical data assets.",
      },
      {
        id: "q6",
        text: "Every key dataset has a clearly assigned owner or steward.",
      },
    ],
  },
  {
    id: "policy",
    label: "Governance Policy & Ownership",
    questions: [
      {
        id: "q7",
        text: "We have written data governance policies that are actively enforced.",
      },
      {
        id: "q8",
        text: "Roles and responsibilities for data governance are clearly defined.",
      },
      {
        id: "q9",
        text: "There is an established process for resolving data ownership disputes.",
      },
    ],
  },
  {
    id: "ai",
    label: "AI Governance Readiness",
    questions: [
      {
        id: "q10",
        text: "We assess AI/ML models for bias, fairness, and risk before deployment.",
      },
      {
        id: "q11",
        text: "AI system decisions can be explained and audited when required.",
      },
      {
        id: "q12",
        text: "There is executive-level oversight of AI governance and risk.",
      },
    ],
  },
  {
    id: "risk",
    label: "Compliance & Risk Management",
    questions: [
      {
        id: "q13",
        text: "We can demonstrate compliance with relevant data regulations on request.",
      },
      {
        id: "q14",
        text: "We have a documented process for responding to data incidents or breaches.",
      },
      {
        id: "q15",
        text: "Data risk is reviewed and reported to leadership on a regular basis.",
      },
    ],
  },
];

const ALL_QUESTIONS = CATEGORIES.flatMap((c) =>
  c.questions.map((q) => ({ ...q, cat: c.id }))
);
const TOTAL_Q = ALL_QUESTIONS.length;

function tierFor(pct: number) {
  if (pct < 26)
    return {
      name: "Reactive",
      desc: "Governance is largely absent or informal. Data issues are handled case-by-case, with no consistent ownership or process.",
    };
  if (pct < 51)
    return {
      name: "Emerging",
      desc: "Some governance elements exist, but they're inconsistent across teams and not yet institutionalized.",
    };
  if (pct < 76)
    return {
      name: "Managed",
      desc: "Core governance processes are defined and mostly followed, with room to tighten monitoring and enforcement.",
    };
  return {
    name: "Optimized",
    desc: "Governance is embedded, actively monitored, and treated as a business-critical discipline.",
  };
}

const FONT_IMPORT =
  "@import url('https://fonts.googleapis.com/css2?family=Fraunces:opsz,wght@9..144,400;9..144,600;9..144,700&family=IBM+Plex+Sans:wght@400;500;600&family=IBM+Plex+Mono:wght@400;500&display=swap');";

// TODO: replace with your own Formspree endpoint (sign up free at formspree.io,
// create a form, and paste its endpoint URL here). Every completed assessment
// will POST here, which triggers an email to you automatically.
const FORMSPREE_ENDPOINT = "https://formspree.io/f/mkjnjvad";

export default function GovernanceAssessment() {
  const [step, setStep] = useState<number>(-1); // -1 = intro, 0..4 = categories, 5 = lead capture, 6 = results
  const [answers, setAnswers] = useState<Record<string, number>>({});
  const [leadName, setLeadName] = useState<string>("");
  const [leadEmail, setLeadEmail] = useState<string>("");
  const [submitting, setSubmitting] = useState<boolean>(false);

  const answeredCount = Object.keys(answers).length;
  const progressPct = Math.round((answeredCount / TOTAL_Q) * 100);

  const categoryScores = useMemo(() => {
    return CATEGORIES.map((cat) => {
      const vals = cat.questions
        .map((q) => answers[q.id])
        .filter((v) => v !== undefined);
      const avg = vals.length
        ? vals.reduce((a, b) => a + b, 0) / vals.length
        : 0;
      return {
        id: cat.id,
        label: cat.label,
        pct: Math.round((avg / 3) * 100),
        rawAvg: avg,
      };
    });
  }, [answers]);

  const overallPct = useMemo(() => {
    if (!categoryScores.length) return 0;
    return Math.round(
      categoryScores.reduce((a, c) => a + c.pct, 0) / categoryScores.length
    );
  }, [categoryScores]);

  const tier = tierFor(overallPct);

  function setAnswer(qid: string, val: number) {
    setAnswers((prev) => ({ ...prev, [qid]: val }));
  }

  function goNext() {
    if (step < 4) setStep(step + 1);
    else setStep(5);
  }
  function goBack() {
    if (step > -1) setStep(step - 1);
  }
  function restart() {
    setAnswers({});
    setLeadName("");
    setLeadEmail("");
    setStep(-1);
  }

  async function submitLead() {
    setSubmitting(true);
    try {
      await fetch(FORMSPREE_ENDPOINT, {
        method: "POST",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          name: leadName,
          email: leadEmail,
          _replyto: leadEmail,
          overallScore: `${overallPct}%`,
          tier: tier.name,
          categoryBreakdown: categoryScores
            .map((c) => `${c.label}: ${c.pct}%`)
            .join(" | "),
        }),
      });
    } catch (err) {
      // If the submission fails (e.g. endpoint not yet configured), don't block
      // the visitor from seeing their own results — just log it for debugging.
      console.error("Lead submission failed:", err);
    }
    setSubmitting(false);
    setStep(6);
  }

  const currentCategory = step >= 0 && step <= 4 ? CATEGORIES[step] : null;
  const currentCategoryAnswered = currentCategory
    ? currentCategory.questions.every((q) => answers[q.id] !== undefined)
    : false;
  const emailValid = /\S+@\S+\.\S+/.test(leadEmail);

  return (
    <div className="gma-root">
      <style>{`
        ${FONT_IMPORT}
        .gma-root {
          --ink: #14182E;
          --ink-soft: #4A4F6A;
          --parchment: #F6F3EB;
          --parchment-deep: #EFE9DA;
          --brass: #A9812C;
          --brass-light: #C9A857;
          --rule: #D9D2BE;
          --risk: #9B3B3B;
          --managed: #3F6B4C;
          --white: #FFFFFF;
          font-family: 'IBM Plex Sans', sans-serif;
          background: var(--parchment);
          color: var(--ink);
          min-height: 100%;
          padding: 0;
          box-sizing: border-box;
        }
        .gma-root *, .gma-root *::before, .gma-root *::after { box-sizing: border-box; }
        .gma-shell {
          max-width: 780px;
          margin: 0 auto;
          padding: 40px 24px 64px;
        }
        .gma-topline {
          display: flex;
          align-items: baseline;
          justify-content: space-between;
          border-bottom: 1px solid var(--rule);
          padding-bottom: 14px;
          margin-bottom: 36px;
        }
        .gma-brand {
          font-family: 'Fraunces', serif;
          font-size: 15px;
          letter-spacing: 0.06em;
          text-transform: uppercase;
          color: var(--ink);
        }
        .gma-brand-sub {
          font-family: 'IBM Plex Mono', monospace;
          font-size: 11px;
          color: var(--brass);
          letter-spacing: 0.05em;
        }
        .gma-hero-title {
          font-family: 'Fraunces', serif;
          font-weight: 600;
          font-size: clamp(28px, 4.5vw, 42px);
          line-height: 1.12;
          margin: 0 0 16px;
          color: var(--ink);
        }
        .gma-hero-sub {
          font-size: 16px;
          line-height: 1.6;
          color: var(--ink-soft);
          max-width: 56ch;
          margin: 0 0 32px;
        }
        .gma-meta-row {
          display: flex;
          gap: 28px;
          margin-bottom: 36px;
          font-family: 'IBM Plex Mono', monospace;
          font-size: 12px;
          color: var(--ink-soft);
        }
        .gma-meta-row strong { color: var(--ink); display: block; font-size: 15px; font-family: 'IBM Plex Sans', sans-serif; }
        .gma-btn {
          font-family: 'IBM Plex Sans', sans-serif;
          font-weight: 600;
          font-size: 14px;
          letter-spacing: 0.02em;
          padding: 13px 26px;
          border-radius: 3px;
          border: 1px solid var(--ink);
          background: var(--ink);
          color: var(--parchment);
          cursor: pointer;
          transition: transform 0.15s ease, background 0.15s ease;
        }
        .gma-btn:hover { background: #292E4D; }
        .gma-btn:active { transform: scale(0.98); }
        .gma-btn[disabled] { opacity: 0.35; cursor: not-allowed; }
        .gma-btn-ghost {
          background: transparent;
          color: var(--ink);
          border: 1px solid var(--rule);
        }
        .gma-btn-ghost:hover { background: var(--parchment-deep); }
        .gma-btn-row { display: flex; gap: 12px; align-items: center; margin-top: 32px; }
        .gma-input {
          font-family: 'IBM Plex Sans', sans-serif;
          font-size: 14.5px;
          padding: 13px 14px;
          border-radius: 3px;
          border: 1px solid var(--rule);
          background: var(--white);
          color: var(--ink);
        }
        .gma-input:focus { outline: none; border-color: var(--brass); }

        .gma-section-num {
          font-family: 'IBM Plex Mono', monospace;
          font-size: 12px;
          color: var(--brass);
          letter-spacing: 0.08em;
          margin-bottom: 8px;
        }
        .gma-cat-title {
          font-family: 'Fraunces', serif;
          font-size: 26px;
          font-weight: 600;
          margin: 0 0 28px;
        }
        .gma-question {
          border-top: 1px solid var(--rule);
          padding: 22px 0;
        }
        .gma-question:last-child { border-bottom: 1px solid var(--rule); }
        .gma-question-text {
          font-size: 15.5px;
          line-height: 1.5;
          margin: 0 0 16px;
          color: var(--ink);
        }
        .gma-options {
          display: grid;
          grid-template-columns: repeat(4, 1fr);
          gap: 8px;
        }
        @media (max-width: 640px) {
          .gma-options { grid-template-columns: 1fr 1fr; }
        }
        .gma-option {
          font-family: 'IBM Plex Sans', sans-serif;
          font-size: 12.5px;
          line-height: 1.3;
          text-align: left;
          padding: 10px 12px;
          border-radius: 3px;
          border: 1px solid var(--rule);
          background: var(--white);
          color: var(--ink-soft);
          cursor: pointer;
          transition: border-color 0.15s ease, background 0.15s ease, color 0.15s ease;
        }
        .gma-option:hover { border-color: var(--brass-light); }
        .gma-option.active {
          border-color: var(--brass);
          background: var(--ink);
          color: var(--parchment);
        }

        .gma-progress-wrap {
          display: flex;
          align-items: center;
          gap: 16px;
          margin-bottom: 8px;
        }
        .gma-progress-track {
          flex: 1;
          height: 2px;
          background: var(--rule);
          position: relative;
        }
        .gma-progress-fill {
          position: absolute;
          left: 0; top: 0; bottom: 0;
          background: var(--brass);
          transition: width 0.3s ease;
        }
        .gma-progress-label {
          font-family: 'IBM Plex Mono', monospace;
          font-size: 11px;
          color: var(--ink-soft);
          white-space: nowrap;
        }
        .gma-steps-label {
          font-family: 'IBM Plex Mono', monospace;
          font-size: 11px;
          color: var(--ink-soft);
          margin-bottom: 28px;
        }

        .gma-seal-wrap { display: flex; justify-content: center; margin: 8px 0 32px; }
        .gma-tier-name {
          font-family: 'Fraunces', serif;
          font-weight: 700;
          font-size: 13px;
        }

        .gma-results-header { text-align: center; margin-bottom: 8px; }
        .gma-results-score {
          font-family: 'IBM Plex Mono', monospace;
          font-size: 13px;
          color: var(--ink-soft);
          text-align: center;
          margin-bottom: 40px;
        }
        .gma-tier-desc {
          text-align: center;
          max-width: 50ch;
          margin: 0 auto 40px;
          font-size: 15px;
          line-height: 1.6;
          color: var(--ink-soft);
        }

        .gma-chart-card {
          background: var(--white);
          border: 1px solid var(--rule);
          border-radius: 4px;
          padding: 24px 8px;
          margin-bottom: 32px;
        }

        .gma-cat-list { margin-bottom: 40px; }
        .gma-cat-row {
          display: flex;
          align-items: center;
          gap: 16px;
          padding: 14px 0;
          border-top: 1px solid var(--rule);
        }
        .gma-cat-row:last-child { border-bottom: 1px solid var(--rule); }
        .gma-cat-row-label {
          flex: 1;
          font-size: 14px;
          color: var(--ink);
        }
        .gma-cat-row-bar {
          width: 140px;
          height: 6px;
          background: var(--parchment-deep);
          border-radius: 3px;
          overflow: hidden;
          position: relative;
        }
        .gma-cat-row-bar-fill { position: absolute; left: 0; top: 0; bottom: 0; border-radius: 3px; }
        .gma-cat-row-pct {
          font-family: 'IBM Plex Mono', monospace;
          font-size: 12px;
          width: 38px;
          text-align: right;
          color: var(--ink-soft);
        }

        .gma-lock-card {
          background: var(--ink);
          color: var(--parchment);
          border-radius: 4px;
          padding: 32px 28px;
          text-align: center;
        }
        .gma-lock-title {
          font-family: 'Fraunces', serif;
          font-size: 21px;
          font-weight: 600;
          margin: 0 0 10px;
        }
        .gma-lock-desc {
          font-size: 14px;
          color: #C7CAE0;
          line-height: 1.6;
          max-width: 46ch;
          margin: 0 auto 24px;
        }
        .gma-lock-btn {
          background: var(--brass);
          border-color: var(--brass);
          color: var(--ink);
        }
        .gma-lock-btn:hover { background: var(--brass-light); }
        .gma-footnote {
          text-align: center;
          font-family: 'IBM Plex Mono', monospace;
          font-size: 11px;
          color: var(--ink-soft);
          margin-top: 20px;
        }
      `}</style>

      <div className="gma-shell">
        <div className="gma-topline">
          <span className="gma-brand">eSense Solutions</span>
          <span className="gma-brand-sub">GOVERNANCE LEDGER™</span>
        </div>

        {step === -1 && (
          <>
            <div className="gma-section-num">
              DATA & AI GOVERNANCE MATURITY ASSESSMENT
            </div>
            <h1 className="gma-hero-title">
              How governed is your data, really?
            </h1>
            <p className="gma-hero-sub">
              A 15-point scored assessment across data quality, metadata,
              governance policy, AI readiness, and compliance risk. Takes about
              4 minutes. You'll get an instant maturity score and a category
              breakdown.
            </p>
            <div className="gma-meta-row">
              <div>
                <strong>15</strong>questions
              </div>
              <div>
                <strong>5</strong>categories
              </div>
              <div>
                <strong>~4 min</strong>to complete
              </div>
            </div>
            <button className="gma-btn" onClick={() => setStep(0)}>
              Begin Assessment →
            </button>
          </>
        )}

        {currentCategory && (
          <>
            <div className="gma-progress-wrap">
              <div className="gma-progress-track">
                <div
                  className="gma-progress-fill"
                  style={{ width: `${progressPct}%` }}
                />
              </div>
              <div className="gma-progress-label">
                {answeredCount}/{TOTAL_Q} answered
              </div>
            </div>
            <div className="gma-steps-label">SECTION {step + 1} OF 5</div>

            <div className="gma-section-num">0{step + 1}</div>
            <h2 className="gma-cat-title">{currentCategory.label}</h2>

            {currentCategory.questions.map((q) => (
              <div className="gma-question" key={q.id}>
                <p className="gma-question-text">{q.text}</p>
                <div className="gma-options">
                  {SCALE.map((opt) => (
                    <button
                      key={opt.v}
                      className={`gma-option${
                        answers[q.id] === opt.v ? " active" : ""
                      }`}
                      onClick={() => setAnswer(q.id, opt.v)}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
              </div>
            ))}

            <div className="gma-btn-row">
              <button className="gma-btn gma-btn-ghost" onClick={goBack}>
                ← Back
              </button>
              <button
                className="gma-btn"
                disabled={!currentCategoryAnswered}
                onClick={goNext}
              >
                {step === 4 ? "See My Results →" : "Next Section →"}
              </button>
            </div>
          </>
        )}

        {step === 5 && (
          <>
            <div className="gma-section-num">ALMOST THERE</div>
            <h2 className="gma-cat-title">See your results</h2>
            <p className="gma-hero-sub" style={{ marginBottom: "28px" }}>
              Enter your details and we'll show your governance maturity score
              immediately — we'll also send you a copy so you don't lose it.
            </p>
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                gap: "14px",
                maxWidth: "420px",
                marginBottom: "8px",
              }}
            >
              <input
                type="text"
                placeholder="Your name"
                value={leadName}
                onChange={(e) => setLeadName(e.target.value)}
                className="gma-input"
              />
              <input
                type="email"
                placeholder="Work email"
                value={leadEmail}
                onChange={(e) => setLeadEmail(e.target.value)}
                className="gma-input"
              />
            </div>
            <div className="gma-btn-row">
              <button className="gma-btn gma-btn-ghost" onClick={goBack}>
                ← Back
              </button>
              <button
                className="gma-btn"
                disabled={!leadName.trim() || !emailValid || submitting}
                onClick={submitLead}
              >
                {submitting ? "Submitting..." : "Show My Results →"}
              </button>
            </div>
            <p
              className="gma-footnote"
              style={{ textAlign: "left", marginTop: "18px" }}
            >
              We'll only use this to send your results and relevant governance
              insights. No spam.
            </p>
          </>
        )}

        {step === 6 && (
          <>
            <div className="gma-seal-wrap">
              <Seal pct={overallPct} tier={tier.name} />
            </div>
            <div className="gma-results-header">
              <div className="gma-section-num" style={{ textAlign: "center" }}>
                YOUR RESULT
              </div>
              <h2 className="gma-cat-title" style={{ textAlign: "center" }}>
                {tier.name}
              </h2>
            </div>
            <div className="gma-results-score">
              Overall Governance Maturity — {overallPct}%
            </div>
            <p className="gma-tier-desc">{tier.desc}</p>

            <div className="gma-chart-card">
              <ResponsiveContainer width="100%" height={280}>
                <RadarChart data={categoryScores} outerRadius="72%">
                  <PolarGrid stroke="#D9D2BE" />
                  <PolarAngleAxis
                    dataKey="label"
                    tick={{
                      fill: "#14182E",
                      fontSize: 11,
                      fontFamily: "IBM Plex Sans",
                    }}
                  />
                  <PolarRadiusAxis
                    angle={90}
                    domain={[0, 100]}
                    tick={false}
                    axisLine={false}
                  />
                  <Radar
                    dataKey="pct"
                    stroke="#A9812C"
                    fill="#A9812C"
                    fillOpacity={0.35}
                    strokeWidth={2}
                  />
                </RadarChart>
              </ResponsiveContainer>
            </div>

            <div className="gma-cat-list">
              {categoryScores.map((c) => (
                <div className="gma-cat-row" key={c.id}>
                  <div className="gma-cat-row-label">{c.label}</div>
                  <div className="gma-cat-row-bar">
                    <div
                      className="gma-cat-row-bar-fill"
                      style={{
                        width: `${c.pct}%`,
                        background:
                          c.pct < 40
                            ? "var(--risk)"
                            : c.pct < 75
                            ? "var(--brass)"
                            : "var(--managed)",
                      }}
                    />
                  </div>
                  <div className="gma-cat-row-pct">{c.pct}%</div>
                </div>
              ))}
            </div>

            <div className="gma-lock-card">
              <div className="gma-lock-title">Your full report is ready</div>
              <p className="gma-lock-desc">
                Unlock a detailed breakdown of every category, specific
                remediation priorities, and a 90-day governance roadmap tailored
                to your score — in a free 30-minute call with our team.
              </p>
              <button
                className="gma-btn gma-lock-btn"
                onClick={() =>
                  window.open(
                    "https://calendly.com/esensesolutions-info/30min",
                    "_blank"
                  )
                }
              >
                Book My Full Report Call
              </button>
            </div>

            <div className="gma-btn-row" style={{ justifyContent: "center" }}>
              <button className="gma-btn gma-btn-ghost" onClick={restart}>
                Retake Assessment
              </button>
            </div>
            <div className="gma-footnote">
              eSense Solutions · Governance Ledger™ Assessment
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function Seal({ pct, tier }: { pct: number; tier: string }) {
  const r = 54;
  const c = 2 * Math.PI * r;
  const dash = (pct / 100) * c;
  return (
    <svg width="160" height="160" viewBox="0 0 160 160">
      <circle
        cx="80"
        cy="80"
        r="70"
        fill="none"
        stroke="#D9D2BE"
        strokeWidth="1"
        strokeDasharray="2 4"
      />
      <circle
        cx="80"
        cy="80"
        r="62"
        fill="#F6F3EB"
        stroke="#14182E"
        strokeWidth="1.5"
      />
      <circle
        cx="80"
        cy="80"
        r={r}
        fill="none"
        stroke="#A9812C"
        strokeWidth="3"
        strokeDasharray={`${dash} ${c - dash}`}
        strokeLinecap="round"
        transform="rotate(-90 80 80)"
      />
      <text
        x="80"
        y="76"
        textAnchor="middle"
        fontFamily="IBM Plex Mono, monospace"
        fontSize="24"
        fontWeight="500"
        fill="#14182E"
      >
        {pct}%
      </text>
      <text
        x="80"
        y="96"
        textAnchor="middle"
        fontFamily="Fraunces, serif"
        fontWeight="700"
        fontSize="11"
        letterSpacing="0.06em"
        fill="#A9812C"
      >
        {tier.toUpperCase()}
      </text>
    </svg>
  );
}
